/**
 * useStudentData - Centralized hook for fetching and caching student data
 *
 * Quick Load Pattern (see QUICK_LOAD_PATTERN.md):
 * 1. Load from localStorage synchronously (instant display, persists across tab close)
 * 2. Skip Firestore fetch if cache exists (0 reads on navigation)
 * 3. hasAttemptedInitialLoad flag prevents re-fetching when callback deps change
 * 4. Only explicit refresh() invalidates cache and refetches
 *
 * Performance:
 * - First visit: 1 Firestore read (cached to localStorage)
 * - Tab switches: 0 reads (instant from cache)
 * - Page reload: 0 reads (cache persists)
 * - Read reduction: 80-100% after first load
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../../../firebase';
import { studentDataFetcher } from '../../../services/discipline/rules/StudentDataFetcher';
import disciplineConfig from '../../../services/disciplineConfigService';
import { disciplineService } from '../../../services/disciplineService';
import { useNotifications } from '../../../components/notifications/NotificationSystem';

// localStorage cache for student data (Quick Load Pattern - persists across tab close/reload)
const STUDENT_DATA_CACHE_KEY = 'swan_student_data';
const STUDENT_DATA_CACHE_VERSION = 3;
const CACHE_MAX_AGE_MS = 60 * 60 * 1000; // 1 hour TTL
const getStudentDataCacheKey = (schoolId, studentId) => (
  `${STUDENT_DATA_CACHE_KEY}_${schoolId}_${studentId}`
);

/**
 * Save student data to localStorage
 */
const saveDataToCache = (studentId, schoolId, data) => {
  try {
    if (data) {
      const cacheData = {
        version: STUDENT_DATA_CACHE_VERSION,
        studentId,
        schoolId,
        data,
        timestamp: Date.now()
      };
      localStorage.setItem(getStudentDataCacheKey(schoolId, studentId), JSON.stringify(cacheData));
    }
  } catch {
    // Cache writes are an optimisation; live data remains authoritative.
  }
};

/**
 * Load student data from localStorage
 */
const loadDataFromCache = (studentId, schoolId) => {
  const cacheKey = getStudentDataCacheKey(schoolId, studentId);
  try {
    // Version 2 was learner-only and could cross school-selection boundaries.
    localStorage.removeItem(`${STUDENT_DATA_CACHE_KEY}_${studentId}`);
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      const cacheEntry = JSON.parse(cached);
      if (
        cacheEntry.version !== STUDENT_DATA_CACHE_VERSION
        || cacheEntry.studentId !== studentId
        || cacheEntry.schoolId !== schoolId
      ) {
        localStorage.removeItem(cacheKey);
        return null;
      }
      const cacheAgeMs = Date.now() - cacheEntry.timestamp;
      // Expire stale cache
      if (cacheAgeMs > CACHE_MAX_AGE_MS) {
        localStorage.removeItem(cacheKey);
        return null;
      }

      return cacheEntry.data;
    }
  } catch (error) {
    // Clear corrupted cache
    try {
      localStorage.removeItem(cacheKey);
    } catch (e) {
      // Ignore cleanup errors
    }
  }
  return null;
};

export const useStudentData = (studentId, schoolId) => {
  const profileKey = `${schoolId || ''}:${studentId || ''}`;

  // Load from cache SYNCHRONOUSLY before first render (instant display)
  // Also determine initial loading state based on cache existence
  const [initialState] = useState(() => {
    if (studentId) {
      const cached = loadDataFromCache(studentId, schoolId);
      return { cachedData: cached, loading: !cached };
    }
    return { cachedData: null, loading: true };
  });

  const [cachedData, setCachedData] = useState(initialState.cachedData);
  const [loading, setLoading] = useState(initialState.loading);
  const [dataProfileKey, setDataProfileKey] = useState(profileKey);

  const [error, setError] = useState(null);

  // Track initial load attempt to prevent re-fetching (Quick Load Pattern)
  const [hasAttemptedInitialLoad, setHasAttemptedInitialLoad] = useState(false);
  const activeProfileKeyRef = useRef(profileKey);
  const requestGenerationRef = useRef(0);
  activeProfileKeyRef.current = profileKey;

  const { showError } = useNotifications();

  // Fetch all foundational data once
  const fetchStudentData = useCallback(async () => {
    const requestProfileKey = profileKey;
    const requestGeneration = ++requestGenerationRef.current;
    const isCurrentRequest = () => (
      activeProfileKeyRef.current === requestProfileKey
      && requestGenerationRef.current === requestGeneration
    );

    if (!studentId || !schoolId) {
      if (isCurrentRequest()) {
        setLoading(false);
        setHasAttemptedInitialLoad(true);
      }
      return;
    }

    try {
      if (!isCurrentRequest()) return;
      setLoading(true);

      // Clear any previous errors
      setError(null);

      // Parent-link metadata is independent from the timeline/config payload.
      // Start it immediately so first load is the slower branch, not both
      // branches added together.
      const parentLinksPromise = (async () => {
        let isParentUser = false;
        const currentUser = auth.currentUser;
        if (currentUser) {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            isParentUser = userDoc.data().parent === true;
          }
        }

        let parentLinksSnapshot;
        if (isParentUser) {
          // Parent users: fetch ALL parent links and filter client-side
          const allLinksQuery = query(
            collection(db, 'parentChildLinks'),
            where('schoolId', '==', schoolId)
            // No studentId filter for parents
          );
          const allSnapshot = await getDocs(allLinksQuery);

          // Filter client-side for this student and active status
          const filteredDocs = allSnapshot.docs.filter(linkDoc => {
            const data = linkDoc.data();
            return data.studentId === studentId && data.status === 'active';
          });

          parentLinksSnapshot = { docs: filteredDocs };
        } else {
          // Staff users: use normal where clause query
          parentLinksSnapshot = await getDocs(query(
            collection(db, 'parentChildLinks'),
            where('schoolId', '==', schoolId),
            where('studentId', '==', studentId),
            where('status', '==', 'active')
          ));
        }

        const parentLinksData = parentLinksSnapshot.docs.map(linkDoc => ({
          id: linkDoc.id,
          ...linkDoc.data()
        }));

        return Promise.all(
          parentLinksData.map(async (link) => {
            try {
              const parentDoc = await getDoc(doc(db, 'users', link.parentId));
              const parentData = parentDoc.exists() ? parentDoc.data() : {};

              return {
                id: link.id,
                parentId: link.parentId,
                parentName: parentData.displayName || parentData.name || 'Unknown Parent',
                relationship: {
                  type: link.relationship || 'Parent'
                },
                contactInfo: {
                  email: link.email?.address || parentData.email,
                  phone: link.phone || parentData.phone || parentData.phoneNumber
                },
                telegram: link.telegram || {},
                status: link.status,
                linkedAt: link.linkedAt
              };
            } catch {
              return {
                id: link.id,
                parentId: link.parentId,
                parentName: 'Unknown Parent',
                relationship: { type: link.relationship || 'Parent' },
                contactInfo: { email: link.email?.address },
                telegram: link.telegram || {},
                status: link.status
              };
            }
          })
        );
      })();

      // Fetch all raw data in parallel
      const [
        disciplineData,
        eventTypeConfig,
        categories,
        parentLinks
      ] = await Promise.all([
        // Use StudentDataFetcher for mentions + consequences + messages (cached with 5min TTL)
        studentDataFetcher.fetchStudentData(studentId, schoolId),

        // Fetch event type config
        disciplineConfig.getEventTypeConfig(schoolId),

        // Fetch categories
        disciplineService.getDisciplineCategories(schoolId),

        parentLinksPromise
      ]);

      if (!isCurrentRequest()) return;

      // Store all cached data
      const newCachedData = {
        mentions: disciplineData.mentions,
        // Phase 3 (custom-domains): full per-domain map and the active-domain
        // registry the events came from. `mentions` stays for backward compat
        // (it's the same array as `domainEvents.discipline`).
        domainEvents: disciplineData.domainEvents || {},
        domains: disciplineData.domains || [],
        consequences: disciplineData.consequences,
        messages: disciplineData.messages,
        parentLinks,
        eventTypeConfig,
        categories,
        fetchedAt: new Date(),
        stats: disciplineData.stats
      };

      setCachedData(newCachedData);
      saveDataToCache(studentId, schoolId, newCachedData); // Cache for next page load

    } catch (error) {
      if (!isCurrentRequest()) return;
      // Handle access denied errors differently (don't show error notification)
      if (error.code === 'ACCESS_DENIED') {
        setError('ACCESS_DENIED');
      } else {
        setError(error.message || 'Failed to load student data');
        showError('Failed to load student data');
      }

      // Clear any cached data on error
      setCachedData(null);
    } finally {
      if (isCurrentRequest()) {
        setLoading(false);
        setHasAttemptedInitialLoad(true);
      }
    }
  }, [studentId, schoolId, profileKey, showError]);

  // A route-param change reuses this hook instance. Re-key every piece of
  // profile state before another learner's data can be returned.
  useEffect(() => {
    if (dataProfileKey === profileKey) return;

    const nextCachedData = studentId ? loadDataFromCache(studentId, schoolId) : null;
    setDataProfileKey(profileKey);
    setCachedData(nextCachedData);
    setLoading(Boolean(studentId && schoolId && !nextCachedData));
    setError(null);
    setHasAttemptedInitialLoad(false);
  }, [dataProfileKey, profileKey, schoolId, studentId]);

  const profileMatches = dataProfileKey === profileKey;
  const activeCachedData = profileMatches ? cachedData : null;
  const activeLoading = profileMatches ? loading : true;
  const activeError = profileMatches ? error : null;

  // Initial load with skip logic (Quick Load Pattern)
  useEffect(() => {
    if (!profileMatches) return;

    // Exit early if already attempted
    if (hasAttemptedInitialLoad) return;

    // Skip Firestore fetch if cache exists
    if (activeCachedData) {
      setHasAttemptedInitialLoad(true);
      setLoading(false);
      return;
    }

    fetchStudentData();
  }, [profileMatches, hasAttemptedInitialLoad, activeCachedData, fetchStudentData]);

  // Explicit refresh function (invalidates cache and refetches)
  const refresh = useCallback(async () => {
    // Clear localStorage cache
    try {
      localStorage.removeItem(getStudentDataCacheKey(schoolId, studentId));
    } catch (e) {
      // Ignore
    }

    // Invalidate StudentDataFetcher cache
    studentDataFetcher.invalidateCache(studentId);

    // Reset state and refetch
    setCachedData(null);
    setHasAttemptedInitialLoad(false);
    setLoading(true);
    await fetchStudentData();
  }, [studentId, schoolId, fetchStudentData]);

  return {
    cachedData: activeCachedData,
    loading: activeLoading,
    error: activeError,
    refresh
  };
};
